import React from 'react';
import {
  AutocompleteDropdown,
  Box,
  Controller,
  ControllerWrapper,
  DateInput,
  FieldWrapper,
  formatSnakeCaseToGeneral,
  FormFieldsContainer,
  Label,
  RegularField,
  useFormContext,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';

import { IAuthorizedUserDtoRelationshipTypeEnum } from '@/dtos';

import { AuthorizedUserFormValues, FormFields } from './hooks/useAuthorizedUserForm';

const RELATIONSHIP_OPTIONS = Object.values(IAuthorizedUserDtoRelationshipTypeEnum).map((value) => ({
  key: value,
  value,
  title: capitalize(formatSnakeCaseToGeneral(value)),
}));

const LABEL_WIDTH = { width: '100px', min: '100px' };
const INPUT_WIDTH = '320px';

interface FieldConfig {
  name: FormFields;
  label: string;
  placeholder: string;
  type: 'text' | 'dropdown' | 'date';
  testId: string;
}

const FIELDS: FieldConfig[] = [
  {
    name: FormFields.firstName,
    label: 'First name',
    placeholder: 'Enter first name',
    type: 'text',
    testId: 'authorized-users-modal_first-name',
  },
  {
    name: FormFields.lastName,
    label: 'Last name',
    placeholder: 'Enter last name',
    type: 'text',
    testId: 'authorized-users-modal_last-name',
  },
  {
    name: FormFields.relationshipType,
    label: 'Relationship',
    placeholder: 'Choose relationship',
    type: 'dropdown',
    testId: 'authorized-users-modal_relationship-type',
  },
  {
    name: FormFields.birthOn,
    label: 'Date of Birth',
    placeholder: 'MM/DD/YYYY',
    type: 'date',
    testId: 'authorized-users-modal_birth-on',
  },
];

const AuthorizedUsersForm = () => {
  const { control, formState: { errors } } = useFormContext<AuthorizedUserFormValues>();

  return (
    <FormFieldsContainer>
      {FIELDS.map(({ name, label, placeholder, type, testId }) => {
        const errorText = errors?.[name]?.message;

        return (
          <FieldWrapper key={name}>
            <Box direction="row" align="center" justify="between" width="100%">
              <Label name={name} showRequireIcon width={LABEL_WIDTH}>
                {label}
              </Label>
              <ControllerWrapper
                style={type === 'date' ? { flex: 'none', width: INPUT_WIDTH } : { flex: 'none' }}
                errorText={type === 'dropdown' ? undefined : errorText}
              >
                <Controller
                  name={name}
                  control={control}
                  render={({ field }) => {
                    if (type === 'dropdown') {
                      return (
                        <AutocompleteDropdown
                          name={name}
                          testId={testId}
                          onChange={field.onChange}
                          options={RELATIONSHIP_OPTIONS}
                          value={field.value}
                          defaultValue={field.value}
                          placeholder={placeholder}
                          errorText={errorText}
                          style={{ width: INPUT_WIDTH }}
                        />
                      );
                    }
                    if (type === 'date') {
                      return (
                        <DateInput
                          name={name}
                          testId={testId}
                          maxDate={dayjs()}
                          isManualDateInput
                          selectDateWithoutApply
                          placeholder={placeholder}
                          onChange={field.onChange}
                          value={field.value}
                          isError={!!errorText}
                          style={{ width: '100%' }}
                          isPicker
                        />
                      );
                    }
                    return (
                      <RegularField
                        name={name}
                        testId={testId}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder={placeholder}
                        isError={!!errorText}
                        style={{ width: INPUT_WIDTH }}
                        editMode
                      />
                    );
                  }}
                />
              </ControllerWrapper>
            </Box>
          </FieldWrapper>
        );
      })}
    </FormFieldsContainer>
  );
};

export default AuthorizedUsersForm;
